/**
 * Scrub credentials out of log records before they leave the browser.
 *
 * Wired in as `logs.beforeSend` in `./posthog.ts`. It exists because
 * `logs.captureConsoleLogs` is enabled: every `console.*` call in this app and in any dependency
 * now becomes a log record, and no review process can guarantee that nobody ever prints a token.
 * This is the browser counterpart to `redact_event()` in `api/utils/logging.py`.
 *
 * Pattern-based by necessity — console arguments arrive already flattened into a string, so
 * there are no structured fields left to match on by key.
 */
import type { CaptureLogOptions } from "posthog-js";

export const REDACTED = "[REDACTED]";

/**
 * posthog-js prefixes its own console output with this.
 *
 * With console capture on, an SDK warning (a dropped-record notice, a failed request) would
 * itself become a log record, which can produce more warnings. Whether the SDK already excludes
 * its own output could not be confirmed from the minified build, so this guards it either way —
 * the same reasoning as `_ExporterLoopFilter` on the API side. Costs nothing if redundant.
 */
const SDK_LOG_PREFIX = "[PostHog.js]";

const PATTERNS: RegExp[] = [
  // JSON Web Tokens: header.payload.signature, base64url. Covers our access and refresh
  // tokens, and PostHog's identity hash is caught by the key-value rule below.
  /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]+/g,
  // "Bearer <token>" in any casing.
  /\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi,
  // key: "value" / key=value / "key":"value" for credential-ish key names. The key is kept so
  // the log still shows what was elided.
  /(["']?\b(?:password|passwd|secret|token|api[_-]?key|authorization|auth|credential|identity_hash|posthog_identity_hash|client[_-]?secret)\b["']?\s*[:=]\s*)(["']?)[^\s,;}"']+\2/gi,
  // Stripe secret and restricted keys, which must never appear client-side at all.
  /\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]+/g,
];

/** Redact credential patterns from a string. Non-strings pass through untouched. */
export function redactString(value: string): string {
  return PATTERNS.reduce(
    (acc, pattern, index) =>
      // The key-value pattern keeps its key and quote groups; the rest replace wholesale.
      index === 2
        ? acc.replace(pattern, `$1$2${REDACTED}$2`)
        : acc.replace(pattern, REDACTED),
    value
  );
}

function redactValue(value: unknown): unknown {
  if (typeof value === "string") return redactString(value);
  if (Array.isArray(value)) return value.map(redactValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        redactValue(v),
      ])
    );
  }
  return value;
}

/**
 * `beforeSend` hook. Returns the record with credentials masked.
 *
 * Returns null only for PostHog's own SDK output, to avoid a self-feeding loop. A record that
 * merely trips a redaction pattern is still worth keeping, just without the secret — dropping
 * those would hide real failures.
 */
export function redactLogRecord(
  record: CaptureLogOptions
): CaptureLogOptions | null {
  try {
    if (
      typeof record.body === "string" &&
      record.body.startsWith(SDK_LOG_PREFIX)
    ) {
      return null;
    }
    return {
      ...record,
      body:
        typeof record.body === "string"
          ? redactString(record.body)
          : record.body,
      attributes: record.attributes
        ? (redactValue(record.attributes) as CaptureLogOptions["attributes"])
        : record.attributes,
    };
  } catch {
    // Deliberately does not touch `record` again: whatever threw above (a hostile getter, a
    // circular structure) would throw here too and escape this handler, and posthog-js drops
    // any record whose beforeSend throws. Returning a bare body fails closed instead.
    return { body: REDACTED };
  }
}
